/**
 * @fileoverview Component for displaying a list of chips with expand/collapse functionality
 */
import React, { useState, useCallback } from "react";
import PropTypes from "prop-types";
import { Box, Chip, Tooltip } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { intl } from "dmsbasereact";

export const ExpandableChipList = ({
  items = [],
  labelKey = null,
  maxVisible = 2,
  size = "small",
  variant = "outlined",
  color = "default"}) => {
  const [expanded, setExpanded] = useState(false);

  const handleToggle = useCallback((e) => {
    e.stopPropagation();
    setExpanded((prev) => !prev);
  }, []);

  if (!items || items.length === 0) {
    return (
      <Box sx={{ color: "text.secondary", fontSize: "0.875rem" }}>
        {intl.get("NoItems").d("-")}
      </Box>
    );
  }

  const getLabel = (item) => {
    if (typeof item === "string") return item;
    if (labelKey && item[labelKey]) return item[labelKey];
    return item.name || item.label || item.permission_name || item.role_name || String(item);
  };

  const visibleItems = expanded ? items : items.slice(0, maxVisible);
  const hiddenCount = items.length - maxVisible;
  const hasMore = hiddenCount > 0;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, alignItems: "center" }}>
        {visibleItems.map((item, idx) => (
          <Tooltip key={idx} title={getLabel(item)}>
            <Chip
              label={getLabel(item)}
              size={size}
              variant={variant}
              color={color}
              sx={{
                maxWidth: "160px",
                "& .MuiChip-label": {
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                },
              }}
            />
          </Tooltip>
        ))}

        {hasMore && (
          <Tooltip
            title={
              expanded
                ? intl.get("ShowLess").d("Show less")
                : intl.get("ShowMore", { count: hiddenCount }).d("Show {count} more")
            }
          >
            <Chip
              label={expanded ? "" : `+${hiddenCount}`}
              size={size}
              color="primary"
              onClick={handleToggle}
              onDelete={handleToggle}
              deleteIcon={
                expanded ? (
                  <ExpandLessIcon fontSize="small" />
                ) : (
                  <ExpandMoreIcon fontSize="small" />
                )
              }
              sx={{
                cursor: "pointer",
                minWidth: expanded ? "32px" : "auto",
                "& .MuiChip-label": {
                  pr: expanded ? 0 : 0.5,
                },
                "& .MuiChip-deleteIcon": {
                  ml: expanded ? 0 : -0.5,
                },
              }}
            />
          </Tooltip>
        )}
      </Box>
    </Box>
  );
};

ExpandableChipList.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        name: PropTypes.string,
        label: PropTypes.string,
        permission_name: PropTypes.string,
        role_name: PropTypes.string,
      }),
    ])
  ),
  labelKey: PropTypes.string,
  maxVisible: PropTypes.number,
  size: PropTypes.oneOf(["small", "medium"]),
  variant: PropTypes.oneOf(["outlined", "filled"]),
  color: PropTypes.string,
};

